import React from "react";
import UseContext from "hooks/useContext";
import { formatTemplateToolsWithProvider } from "pages/missionTemplates/utils/templateTools";
import {
    buildTemplateConditions,
    formatRiskyPositionSummary,
    useRiskyPositions,
} from "pages/missionTemplates/utils/templateRiskyPositions";
import {
    getTemplateQualification,
    getTemplateTitle,
} from "pages/missionTemplates/utils/templateListing";
import {
    formatHourlyRate,
    parseHourlyRate,
} from "pages/missionTemplates/utils/templateMinimumWage";
import {
    buildTasksPayload,
    formatTaskSummary,
    getTemplateTaskLabels,
} from "pages/missionTemplates/utils/templateTasks";

const Row = ({ label, value }) => (
    <div className={"flex justify-between items-start gap-4 py-4 border-b border-gray-100 last:border-b-0"}>
        <div className={"ft-sm text-gray-500 shrink-0"}>{label}</div>
        <div
            className={
                "ft-sm text-gray-900 text-right break-words whitespace-pre-line max-w-[70%] min-w-0"
            }
        >
            {value || "-"}
        </div>
    </div>
);

// Same rows, same order, in the wizard confirmation and the preview panel.
// `emptyKey` names the text shown when the value is missing.
export const SUMMARY_ROWS = [
    { key: "name", defaultLabel: "Intitulé" },
    { key: "activity", defaultLabel: "Secteur" },
    { key: "qualification", defaultLabel: "Qualification" },
    { key: "riskyPosition", defaultLabel: "Poste à risque" },
    { key: "tools", defaultLabel: "Matériel", emptyKey: "none" },
    { key: "licences", defaultLabel: "Permis", emptyKey: "none" },
    { key: "languages", defaultLabel: "Langue", emptyKey: "none" },
    {
        key: "dressCode",
        defaultLabel: "Tenue vestimentaire",
        emptyKey: "dressCodeMissing",
    },
    { key: "address", defaultLabel: "Adresse" },
    { key: "salary", defaultLabel: "Taux horaire brut" },
    { key: "contact", defaultLabel: "Responsable" },
    { key: "tasks", defaultLabel: "Tâches", emptyKey: "noneFeminine" },
];

const SUMMARY_EMPTY_DEFAULTS = {
    none: "Aucun",
    noneFeminine: "Aucune",
    dressCodeMissing: "Non renseignée",
};

export const getSummaryRowLabel = (language, row) =>
    language.getValue(
        `missionTemplate.preview.fields.${row.key}`,
        row.defaultLabel
    );

export const getSummaryRowValue = (language, row, summary) => {
    const value = summary?.[row.key];
    if (value || !row.emptyKey) return value;
    return language.getValue(
        `missionTemplate.summary.${row.emptyKey}`,
        SUMMARY_EMPTY_DEFAULTS[row.emptyKey]
    );
};

export const formatList = (list) =>
    Array.isArray(list) && list.length > 0 ? list.join(", ") : "";

export const formatAddress = (location) => {
    if (!location) return "";
    if (typeof location === "string") return location;
    const { street, postcode, city, department } = location;
    return [street, postcode, city, department].filter(Boolean).join(" ");
};

// A rate the wizard cannot parse is shown as typed rather than hidden.
const formatSalary = (salary, isIndexed, language) => {
    if (salary === undefined || salary === null || salary === "") return "";
    const rate = parseHourlyRate(salary);
    const amount = `${rate === null ? String(salary) : formatHourlyRate(rate)} €`;
    if (!isIndexed) return amount;
    return `${amount} · ${language.getValue(
        "missionTemplate.summary.indexedToMinimumWage",
        "indexé au SMIC"
    )}`;
};

const formatContact = (name, poste) =>
    name ? `${name}${poste ? ` · ${poste}` : ""}` : "";

/** Wizard state (templateData), for the confirmation step. */
export const buildSummaryFromWizardData = (
    templateData,
    language,
    riskyPositions
) => ({
    name: templateData?.name,
    activity: templateData?.activity,
    qualification: templateData?.qualification,
    riskyPosition: formatRiskyPositionSummary(
        buildTemplateConditions(templateData),
        language,
        riskyPositions
    ),
    tools: formatTemplateToolsWithProvider(templateData?.tools, language),
    licences: formatList(templateData?.licences),
    languages: formatList(templateData?.languages),
    dressCode: templateData?.dressCode?.trim(),
    address: formatAddress(templateData?.location),
    salary: formatSalary(
        templateData?.salary,
        templateData?.isIndexedToMinimumWage,
        language
    ),
    contact: formatContact(
        templateData?.contact_name,
        templateData?.contact_poste
    ),
    // Unchecked proposals are left out, as in the payload.
    tasks: formatTaskSummary(
        getTemplateTaskLabels(buildTasksPayload(templateData?.tasks)),
        language
    ),
});

/** Template as read from the api (list, detail), for the preview panel and the mission recap. */
export const buildSummaryFromApiTemplate = (
    template,
    language,
    riskyPositions
) => ({
    name: getTemplateTitle(template),
    activity: template?.activity,
    qualification: getTemplateQualification(template),
    riskyPosition: formatRiskyPositionSummary(
        template?.conditions,
        language,
        riskyPositions
    ),
    tools: formatTemplateToolsWithProvider(template?.tools, language),
    licences: formatList(template?.licences),
    languages: formatList(template?.language || template?.languages),
    dressCode:
        typeof template?.dressCode === "string" ? template.dressCode : "",
    address: formatAddress(template?.location),
    salary: formatSalary(
        template?.salary?.wage_per_hour,
        template?.isIndexedToMinimumWage,
        language
    ),
    contact: formatContact(template?.contact?.name, template?.contact?.poste),
    tasks: formatTaskSummary(getTemplateTaskLabels(template?.tasks), language),
});

export const MissionTemplateSummaryRows = ({ summary }) => {
    const { language } = UseContext();
    if (!summary) return null;
    return (
        <div className={"flex flex-col"}>
            {SUMMARY_ROWS.map((row) => (
                <Row
                    key={row.key}
                    label={getSummaryRowLabel(language, row)}
                    value={getSummaryRowValue(language, row, summary)}
                />
            ))}
        </div>
    );
};

/** Pass either `wizardData` or `apiTemplate`, not both. */
const MissionTemplateSummary = ({ wizardData, apiTemplate }) => {
    const { language } = UseContext();
    const { riskyPositions } = useRiskyPositions();
    const summary =
        wizardData != null
            ? buildSummaryFromWizardData(wizardData, language, riskyPositions)
            : buildSummaryFromApiTemplate(
                  apiTemplate,
                  language,
                  riskyPositions
              );

    return <MissionTemplateSummaryRows summary={summary} />;
};

export default MissionTemplateSummary;
